import React, { useContext } from 'react'
/// React router dom
import { Routes, Route, Outlet, Navigate } from 'react-router-dom'
import { connect } from 'react-redux';
/// Css
import './index.css'
import './chart.css'
import './step.css'

/// Layout
import ConsoleShell from './layouts/console/ConsoleShell'

/// Streams
import StreamDashboard from "./components/Streams/StreamDashboard";
import StreamList from "./components/Streams/StreamList";
import CreateStream from "./components/Streams/CreateStream";
import StreamDetail from "./components/Streams/StreamDetail";
import SocialStreaming from "./pages/SocialStreaming";

/// VOD Management
import MediaDirectoryManager from "./components/VOD/MediaDirectoryManager";
import VODUploader from "./components/VOD/VODUploader";
import PlaylistManagement from "./components/VOD/PlaylistManagement";
import ScheduleManagement from "./components/VOD/ScheduleManagement";

/// IPTV
import IPTVManager from "./components/IPTV/IPTVManager";

/// CDN
import CDNConfigPage from "./components/CDN/CDNConfigPage";

/// Custom Domains
import CustomDomainManager from "./components/Domains/CustomDomainManager";

/// Tickets
import TicketList from "./components/Tickets/TicketList";
import CreateTicket from "./components/Tickets/CreateTicket";
import TicketDetail from "./components/Tickets/TicketDetail";
import AdminTicketQueue from "./components/Tickets/AdminTicketQueue";


/// Admin Pages

import UserManagement from "./pages/admin/UserManagement";
import RoleManagement from "./pages/admin/RoleManagement";
import GeneralSettings from "./pages/admin/GeneralSettings";
import StripeSettings from "./pages/admin/StripeSettings";
import SocialLoginSettings from "./pages/admin/SocialLoginSettings";
import SeoSettings from "./pages/admin/SeoSettings";
import VerificationSettings from "./pages/admin/VerificationSettings";
import BillingTaxSettings from "./pages/admin/BillingTaxSettings";
import AdminInvoices from "./pages/admin/AdminInvoices";
import InvoiceView from "./pages/InvoiceView";
import EmailSettings from "./pages/admin/EmailSettings";
import TwilioSettings from "./pages/admin/TwilioSettings";
import AdminBillingPage from "./pages/admin/AdminBillingPage";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import PaymentReports from "./pages/admin/PaymentReports";
import Analytics from "./pages/admin/Analytics";
import SystemHealth from "./pages/admin/SystemHealth";
import MonetizationSettings from "./components/Monetization/MonetizationSettings";


/// Notification History
import NotificationHistory from "./pages/NotificationHistory";
import ApiKeys from "./pages/developer/ApiKeys";
import DeveloperHub from "./pages/developer/DeveloperHub";
import SwitcherList from "./pages/Switcher/SwitcherList";
import Playout from "./pages/Playout/Playout";
import SwitcherConsole from "./pages/Switcher/SwitcherConsole";
import TeamManagement from "./pages/team/TeamManagement";
import MobileOnlyNotice from "./pages/team/MobileOnlyNotice";




/// Subscription Pages
import Pricing from "./pages/Pricing";
import Billing from "./pages/Billing";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import SubscriptionCancel from "./pages/SubscriptionCancel";

/// Auth
import StreamLogin from "./pages/StreamLogin";
import Register from "./pages/Registration";
import ChangePassword from "./components/Auth/ChangePassword";


/// Error Pages
import Error403 from './pages/Error403'
import Error404 from './pages/Error404'

import { ThemeContext } from "../context/ThemeContext";
//Scroll To Top
import ScrollToTop from './layouts/ScrollToTop';

// Protected Route component for admin-only routes
const ProtectedRoute = ({ children, user, requiredRole = 'admin' }) => {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requiredRole === 'admin' && user.role !== 'admin') {
    return <Navigate to="/stream-dashboard" replace />;
  }
  
  return children;
};

// Guard: redirects to /pricing if user has no subscription
const SubscriptionGuard = ({ children }) => {
  return <Navigate to="/pricing" replace />;
};

// Helper: wrap element with subscription gate
const Gated = ({ subscriptionGated, children }) => {
  if (subscriptionGated) return <Navigate to="/pricing" replace />;
  return children;
};

const Markup = ({ user, subscriptionGated }) => {
  // Team-member accounts only ever use the mobile app — the whole console
  // (nav, billing, everything) is replaced with a single "use the app" screen.
  if (user?.role === 'team_member') {
    return <MobileOnlyNotice name={user?.username} />;
  }
  return (
    <>
      <Routes>
        <Route path='/login' element={<StreamLogin />} />
        <Route path='/register' element={<Register />} />
        <Route path='/page-error-403' element={<Error403/>} />
        <Route path='/page-error-404' element={<Error404/>} />
        <Route path='*' element={subscriptionGated ? <Navigate to="/pricing" replace /> : <Error404/>} />

        <Route element={<MainLayout />}>
          {/* Subscription / billing routes — always accessible when logged in */}
          <Route path='/pricing' element={<Pricing />} />
          <Route path='/billing' element={<Billing />} />
          <Route path='/subscription/success' element={<SubscriptionSuccess />} />
          <Route path='/subscription/cancel' element={<SubscriptionCancel />} />
          <Route path='/change-password' element={<ChangePassword />} />
          <Route path='/monetization' element={<MonetizationSettings />} />
          <Route path='/developer' element={<Gated subscriptionGated={subscriptionGated}><DeveloperHub /></Gated>} />
          <Route path='/developer/api-keys' element={<Gated subscriptionGated={subscriptionGated}><ApiKeys /></Gated>} />

          {/* Main Routes - gated behind subscription */}
          <Route path='/' element={<Gated subscriptionGated={subscriptionGated}><StreamDashboard /></Gated>} />
          <Route path='/stream-dashboard' element={<Gated subscriptionGated={subscriptionGated}><StreamDashboard /></Gated>} />
          <Route path='/switcher' element={<Gated subscriptionGated={subscriptionGated}><SwitcherList /></Gated>} />
          <Route path='/switcher/:id' element={<Gated subscriptionGated={subscriptionGated}><SwitcherConsole /></Gated>} />
          <Route path='/playout' element={<Gated subscriptionGated={subscriptionGated}><Playout /></Gated>} />
          <Route path='/team' element={<Gated subscriptionGated={subscriptionGated}><TeamManagement /></Gated>} />
          <Route path='/streams' element={<Gated subscriptionGated={subscriptionGated}><StreamList /></Gated>} />
          <Route path='/create-stream' element={<Gated subscriptionGated={subscriptionGated}><CreateStream /></Gated>} />
          <Route path='/streams/:id' element={<Gated subscriptionGated={subscriptionGated}><StreamDetail /></Gated>} />
          <Route path='/social-streaming' element={<Gated subscriptionGated={subscriptionGated}><SocialStreaming /></Gated>} />
          <Route path='/analytics' element={<Gated subscriptionGated={subscriptionGated}><Analytics /></Gated>} />

          {/* VOD Management Routes - gated */}
          <Route path='/vod/directories' element={<Gated subscriptionGated={subscriptionGated}><MediaDirectoryManager /></Gated>} />
          <Route path='/vod/uploader' element={<Gated subscriptionGated={subscriptionGated}><VODUploader /></Gated>} />
          <Route path='/vod/playlists' element={<Gated subscriptionGated={subscriptionGated}><PlaylistManagement /></Gated>} />
          <Route path='/vod/schedules' element={<Gated subscriptionGated={subscriptionGated}><ScheduleManagement /></Gated>} />

          {/* IPTV Routes - gated */}
          <Route path='/iptv' element={<Gated subscriptionGated={subscriptionGated}><IPTVManager /></Gated>} />

          {/* CDN Routes - gated */}
          <Route path='/cdn' element={<Gated subscriptionGated={subscriptionGated}><CDNConfigPage /></Gated>} />

          {/* Custom Domain Routes - gated */}
          <Route path='/domains' element={<Gated subscriptionGated={subscriptionGated}><CustomDomainManager /></Gated>} />

          {/* Ticket Routes - gated */}
          <Route path='/tickets' element={<Gated subscriptionGated={subscriptionGated}><TicketList /></Gated>} />
          <Route path='/tickets/new' element={<Gated subscriptionGated={subscriptionGated}><CreateTicket /></Gated>} />
          <Route path='/tickets/:id' element={<Gated subscriptionGated={subscriptionGated}><TicketDetail /></Gated>} />

          {/* Admin Routes - admin only, bypass subscription gate */}
          <Route 
            path='/admin/users' 
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <UserManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path='/admin/roles' 
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <RoleManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path='/admin/settings' 
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <GeneralSettings />
              </ProtectedRoute>
            } 
          />
          <Route
            path='/admin/stripe-settings'
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <StripeSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path='/admin/social-settings'
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <SocialLoginSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path='/admin/seo-settings'
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <SeoSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path='/admin/verification-settings'
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <VerificationSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path='/admin/billing-settings'
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <BillingTaxSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path='/admin/invoices'
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <AdminInvoices />
              </ProtectedRoute>
            }
          />
          <Route path='/billing/invoices/:id' element={<Gated subscriptionGated={subscriptionGated}><InvoiceView /></Gated>} />
          <Route 
            path='/admin/email-settings' 
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <EmailSettings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path='/admin/twilio-settings' 
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <TwilioSettings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path='/admin/tickets' 
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <AdminTicketQueue />
              </ProtectedRoute>
            } 
          />
          <Route
            path='/admin/billing'
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <AdminBillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path='/admin/subscriptions'
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <AdminSubscriptions />
              </ProtectedRoute>
            }
          />
          <Route
            path='/admin/reports'
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <PaymentReports />
              </ProtectedRoute>
            }
          />
          <Route
            path='/admin/analytics'
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <Analytics />
              </ProtectedRoute>
            }
          />
          <Route
            path='/admin/system-health'
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <SystemHealth />
              </ProtectedRoute>
            }
          />
          <Route
            path='/admin/monetization'
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <MonetizationSettings />
              </ProtectedRoute>
            } 
          />

          <Route 
            path='/notifications' 
            element={
              <NotificationHistory />
            } 
          />

        </Route>           
      </Routes>      
      <ScrollToTop />
    </>
  )
}

function MainLayout(){
  return <ConsoleShell />;
}

const mapStateToProps = (state) => {
  return {
    user: state.auth.user,
  };
};

export default connect(mapStateToProps)(Markup);

